import { ResultVariant } from "@/modules/internal_api/image";
import { defineStore } from "pinia";
import { DrawAction } from "@/stores/line";
import type { Line, Point } from "@/stores/line";
import { rbgEditorUndoActionV100, rbgEditorRedoActionV100, rbgEditorZoomImageV100, rbgEditorResetV100 } from "kaleido-event-tracker";
import Flipper from "@/modules/flipper";

export type MagicBrushCacheData = {
  // This is a unique id for the iteration that led the result in this state
  // Will be used on the server to replay the same iteration for the HD result
  iterationId: string;
  // action: DrawAction;
};

export type ImageState = {
  previewVariant: ResultVariant;
  hdVariant: ResultVariant;
  lines: Line[];

  magicBrushCache?: MagicBrushCacheData;
  selectedBackgroundPhotoUrl: string | null;
  uploadedBackgroundPhotoUrl: string | null;
  selectedBackgroundColor: string | null;
  blurRadius: number | null;
  isBackgroundBlurEnabled: boolean;
  isShadowLayerVisible: boolean;
};

export type HistoryImageState = {
  undoStack: ImageState[];
  redoStack: ImageState[];
  current: ImageState;
  zoomLevel: number;
};

export function createPersistentStore(id: string) {
  // Ignore ts warning about no overload matches this call
  //@ts-ignore
  return defineStore(id, {
    state: (): HistoryImageState => ({
      undoStack: [],
      redoStack: [],
      current: {
        previewVariant: ResultVariant.Preview,
        hdVariant: ResultVariant.Hd,
        lines: [],
        magicBrushCache: undefined,
        selectedBackgroundPhotoUrl: null,
        uploadedBackgroundPhotoUrl: null,
        selectedBackgroundColor: null,
        blurRadius: 10,
        isBackgroundBlurEnabled: false,
        isShadowLayerVisible: true,
      },
      zoomLevel: 100,
    }),
    getters: {
      canZoomOut(): boolean {
        return this.zoomLevel > 100;
      },
      canZoomIn(): boolean {
        return this.zoomLevel < 400;
      },
      imageId(): string {
        return id;
      },
      canUndo(): boolean {
        return this.undoStack.length > 0;
      },
      canRedo(): boolean {
        return this.redoStack.length > 0;
      },
      previewVariant(): ResultVariant {
        return this.current.previewVariant;
      },
      hdVariant(): ResultVariant {
        return this.current.hdVariant;
      },
      lines(): Line[] {
        return this.current.lines;
      },
      lastLine(): Line | null {
        if (this.current.lines.length > 0) {
          return this.current.lines[this.current.lines.length - 1];
        } else {
          return null;
        }
      },
      blurRadius(): number {
        return this.current.blurRadius;
      },
      isBackgroundBlurEnabled(): boolean {
        return this.current.isBackgroundBlurEnabled;
      },
      selectedBackgroundColor(): string | null {
        return this.current.selectedBackgroundColor;
      },
      selectedBackgroundPhotoUrl(): string | null {
        return this.current.selectedBackgroundPhotoUrl;
      },
      uploadedBackgroundPhotoUrl(): string | null {
        return this.current.uploadedBackgroundPhotoUrl;
      },
      magicBrushCache(): MagicBrushCacheData | undefined {
        return this.current.magicBrushCache;
      },
      isShadowLayerVisible(): boolean {
        return this.current.isShadowLayerVisible;
      },
    },
    actions: {
      zoomIn(incrementBy: number = 20): void {
        this.setZoom(this.zoomLevel + incrementBy);
        if (Flipper.isEnabled("zoom_events")) {
          rbgEditorZoomImageV100({ image_id: id, mode: "increase", percentage_value: this.zoomLevel });
        }
      },
      zoomOut(decrementBy: number = 20): void {
        this.setZoom(this.zoomLevel - decrementBy);
        if (Flipper.isEnabled("zoom_events")) {
          rbgEditorZoomImageV100({ image_id: id, mode: "decrease", percentage_value: this.zoomLevel });
        }
      },
      setZoom(zoomLevel): void {
        this.zoomLevel = zoomLevel;
      },
      snapshot(): void {
        // console.log("creating history snapshot");
        // deep clone current state and push it to the undo stack
        const clonedState = JSON.parse(JSON.stringify(this.current));
        this.undoStack.push(clonedState);
        // clear the redo stack
        this.redoStack = [];
      },

      snapshotWithState(state: ImageState): void {
        // console.log("creating history snapshot with state");
        // deep clone current state and push it to the undo stack
        const clonedState = JSON.parse(JSON.stringify(state));
        this.undoStack.push(clonedState);
        // clear the redo stack
        this.redoStack = [];
      },

      persist(): void {
        // console.log("persisting");
        this.$persist();
      },

      withSnapshot<T>(fn: () => T): void {
        this.snapshot();
        fn();
        this.persist();
      },

      undo(): void {
        if (this.canUndo) {
          const newCurrent = this.undoStack.pop()!;
          this.redoStack.push(this.current);
          this.current = newCurrent;
          this.$persist();

          window.track("Editor", "undo", "Undo");
          rbgEditorUndoActionV100({ image_id: id });
        }
      },

      redo(): void {
        if (this.canRedo) {
          const newCurrent = this.redoStack.pop()!;
          this.undoStack.push(this.current);
          this.current = newCurrent;
          this.$persist();

          window.track("Editor", "redo", "Redo");
          rbgEditorRedoActionV100({ image_id: id });
        }
      },

      // Reset the image to its inital state
      reset(): void {
        // If the undo stack is not emptt it means that the original state is stored there
        // otherwise the inital state is the current state
        let newCurrent = this.current;
        if (this.canUndo) {
          newCurrent = this.undoStack[0];
        }
        this.undoStack = [];
        this.redoStack = [];
        this.current = newCurrent;
        this.$persist();
        rbgEditorResetV100({ image_id: id });
      },

      cacheMagicBrush(data: MagicBrushCacheData): void {
        this.current.magicBrushCache = { ...this.current.magicBrushCache, ...data };
      },

      selectPreviewVariant(): void {
        this.current.previewVariant = ResultVariant.Preview;
        this.current.hdVariant = ResultVariant.Hd;
      },

      selectMagicBrushVariant(): void {
        this.current.previewVariant = ResultVariant.MagicBrushPreview;
        this.current.hdVariant = ResultVariant.MagicBrushHd;
      },

      startLine(point: Point, action: DrawAction, brushSize: number): void {
        this.current.lines.push({ points: [point], action, brushSize });
      },

      // Add a point to the last/current line
      addLinePoint(point: Point): void {
        const lastLine = this.lastLine;
        if (lastLine) {
          lastLine.points.push(point);
        }
        this.current.lines.splice(this.current.lines.length - 1, 1, lastLine);
      },

      clearLines(): void {
        this.current.lines = [];
      },

      setBlurRadius(radius: number): void {
        this.current.blurRadius = radius;
      },

      setBackgroundBlurEnabled(enabled: boolean): void {
        this.current.isBackgroundBlurEnabled = enabled;
      },

      setSelectedBackgroundColor(color: string): void {
        this.current.selectedBackgroundColor = color;
      },

      setSelectedBackgroundPhotoUrl(url: string): void {
        this.current.selectedBackgroundPhotoUrl = url;
      },

      setUploadedBackgroundPhotoUrl(url: string): void {
        this.current.uploadedBackgroundPhotoUrl = url;
      },

      setShadowLayerVisible(visible: boolean): void {
        this.current.isShadowLayerVisible = visible;
      },
    },
    persist: {
      key: `image-${id}`,
    },
  });
}
